import { supabase } from "./supabase";
import type { Loan } from "@/types/database";

export interface BorrowBookParams {
  copyId: string;
  userId: string;
  dueDays?: number;
}

export interface BorrowBookResult {
  loan: Loan;
  coinCost: number;
  dueDate: Date;
}

export interface ReturnBookParams {
  loanId: string;
  condition?: "OK" | "Damaged" | "Lost";
  returnedBy: string;
}

export interface ReturnBookResult {
  loan: Loan;
  coinReward: number;
  isOnTime: boolean;
  daysLate?: number;
}

// Borrowing configuration
export const BORROWING_CONFIG = {
  defaultLoanDurationDays: 14,
  maxLoansPerStudent: 3,
  coinCosts: {
    borrow: 5, // Cost to borrow a book
  },
  coinRewards: {
    returnOnTime: 15, // Reward for returning on time
    returnLate: 5, // Reduced reward for late return
  },
  penalties: {
    overdueDailyFine: 2, // Coins per day overdue
    damagedBookFine: 20,
    lostBookFine: 50,
  },
};

/**
 * Borrow a book copy for a user
 */
export async function borrowBook({
  copyId,
  userId,
  dueDays = BORROWING_CONFIG.defaultLoanDurationDays,
}: BorrowBookParams): Promise<BorrowBookResult> {
  try {
    // Check if user has reached max loans
    const { data: currentLoans, error: loansError } = await supabase
      .from("loans")
      .select("id")
      .eq("user_id", userId)
      .eq("status", "current");

    if (loansError) throw loansError;

    if (currentLoans.length >= BORROWING_CONFIG.maxLoansPerStudent) {
      throw new Error(
        `You can only borrow up to ${BORROWING_CONFIG.maxLoansPerStudent} books at a time!`,
      );
    }

    // Check if copy is available
    const { data: copy, error: copyError } = await supabase
      .from("copies")
      .select("id, status, book:books(title)")
      .eq("id", copyId)
      .single();

    if (copyError) throw copyError;
    if (!copy) throw new Error("Book copy not found");
    if (copy.status !== "available")
      throw new Error("This book is already checked out");

    // Check student's coin balance (students are in children table)
    const { data: user, error: userError } = await supabase
      .from("children")
      .select("coins")
      .eq("id", userId)
      .single();

    if (userError) throw userError;
    if (!user) throw new Error("User not found");

    const userCoins = user.coins ?? 0;
    if (userCoins < BORROWING_CONFIG.coinCosts.borrow) {
      throw new Error(
        `You need ${BORROWING_CONFIG.coinCosts.borrow} TISAcoins to borrow this book! You have ${userCoins} coins.`,
      );
    }

    // Calculate due date
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + dueDays);

    // Start transaction
    const { data: loan, error: loanError } = await supabase
      .from("loans")
      .insert({
        copy_id: copyId,
        user_id: userId,
        role_borrower: "student",
        due_at: dueDate.toISOString(),
        status: "current",
        overdue_days: 0,
      })
      .select()
      .single();

    if (loanError) throw loanError;

    // Update copy status
    const { error: updateCopyError } = await supabase
      .from("copies")
      .update({ status: "borrowed" })
      .eq("id", copyId);

    if (updateCopyError) throw updateCopyError;

    // Deduct coins and create transaction
    const newBalance = userCoins - BORROWING_CONFIG.coinCosts.borrow;

    const { error: updateUserError } = await supabase
      .from("children")
      .update({ coins: newBalance })
      .eq("id", userId);

    if (updateUserError) throw updateUserError;

    const { error: coinTransactionError } = await supabase
      .from("coin_ledger")
      .insert({
        user_id: userId,
        delta: -BORROWING_CONFIG.coinCosts.borrow,
        type: "borrow",
        note: `Borrowed "${copy.book?.title}" 📚`,
        created_by: userId,
        settled: true,
      });

    if (coinTransactionError) throw coinTransactionError;

    return {
      loan,
      coinCost: BORROWING_CONFIG.coinCosts.borrow,
      dueDate,
    };
  } catch (error) {
    console.error("Error borrowing book:", error);
    throw error;
  }
}

/**
 * Return a book and calculate rewards/penalties
 */
export async function returnBook({
  loanId,
  condition = "OK",
  returnedBy,
}: ReturnBookParams): Promise<ReturnBookResult> {
  try {
    const now = new Date();

    // Get loan details
    const { data: loan, error: loanError } = await supabase
      .from("loans")
      .select(
        `
        id,
        user_id,
        copy_id,
        due_at,
        status,
        copy:copies(
          id,
          book:books(title)
        )
      `,
      )
      .eq("id", loanId)
      .single();

    if (loanError) throw loanError;
    if (!loan) throw new Error("Loan not found");
    if (loan.status !== "current") throw new Error("Book already returned");

    // Get student's current coin balance (students are in children table)
    const { data: studentData, error: studentError } = await supabase
      .from("children")
      .select("coins")
      .eq("id", loan.user_id)
      .single();

    if (studentError) throw studentError;
    const studentCoins = studentData?.coins ?? 0;

    const dueDate = new Date(loan.due_at);
    const isOnTime = now <= dueDate;
    const daysLate = isOnTime
      ? 0
      : Math.ceil((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));

    // Calculate coin reward/penalty
    let coinDelta = 0;
    let transactionNote = "";

    if (condition === "Lost") {
      coinDelta = -BORROWING_CONFIG.penalties.lostBookFine;
      transactionNote = `Book lost: "${loan.copy?.book?.title}" 😢`;
    } else if (condition === "Damaged") {
      coinDelta = -BORROWING_CONFIG.penalties.damagedBookFine;
      transactionNote = `Book damaged: "${loan.copy?.book?.title}" 🔧`;
    } else if (isOnTime) {
      coinDelta = BORROWING_CONFIG.coinRewards.returnOnTime;
      transactionNote = `Returned "${loan.copy?.book?.title}" on time! ⭐`;
    } else {
      coinDelta =
        BORROWING_CONFIG.coinRewards.returnLate -
        daysLate * BORROWING_CONFIG.penalties.overdueDailyFine;
      transactionNote = `Returned "${loan.copy?.book?.title}" (${daysLate} days late) 📅`;
    }

    // Update loan record
    const { error: updateLoanError } = await supabase
      .from("loans")
      .update({
        returned_at: now.toISOString(),
        return_condition: condition,
        status: condition === "Lost" ? "lost" : "returned",
        overdue_days: daysLate,
      })
      .eq("id", loanId);

    if (updateLoanError) throw updateLoanError;

    // Update copy status
    const newCopyStatus = condition === "Lost" ? "lost" : "available";
    const { error: updateCopyError } = await supabase
      .from("copies")
      .update({
        status: newCopyStatus,
        condition: condition,
      })
      .eq("id", loan.copy_id);

    if (updateCopyError) throw updateCopyError;

    // Update student coins and create transaction (students are in children table)
    const newBalance = studentCoins + coinDelta;

    const { error: updateUserError } = await supabase
      .from("children")
      .update({ coins: Math.max(0, newBalance) }) // Prevent negative balance
      .eq("id", loan.user_id);

    if (updateUserError) throw updateUserError;

    if (coinDelta !== 0) {
      const { error: coinTransactionError } = await supabase
        .from("coin_ledger")
        .insert({
          user_id: loan.user_id,
          delta: coinDelta,
          type: isOnTime
            ? "return_ontime"
            : condition === "OK"
              ? "overdue"
              : "lost",
          note: transactionNote,
          created_by: returnedBy,
          settled: true,
        });

      if (coinTransactionError) throw coinTransactionError;
    }

    return {
      loan: {
        ...loan,
        returned_at: now.toISOString(),
        return_condition: condition,
      } as unknown as Loan,
      coinReward: coinDelta,
      isOnTime,
      daysLate: daysLate > 0 ? daysLate : undefined,
    };
  } catch (error) {
    console.error("Error returning book:", error);
    throw error;
  }
}

/**
 * Get user's current loan statistics
 */
export async function getUserLoanStats(userId: string) {
  try {
    const { data: currentLoans, error } = await supabase
      .from("loans")
      .select("id")
      .eq("user_id", userId)
      .eq("status", "current");

    if (error) throw error;

    return {
      currentLoans: currentLoans.length,
      maxLoans: BORROWING_CONFIG.maxLoansPerStudent,
      canBorrowMore: currentLoans.length < BORROWING_CONFIG.maxLoansPerStudent,
    };
  } catch (error) {
    console.error("Error getting loan stats:", error);
    return {
      currentLoans: 0,
      maxLoans: BORROWING_CONFIG.maxLoansPerStudent,
      canBorrowMore: true,
    };
  }
}
